import React, { useCallback, useId } from "react";
import CustomFormField, { getFieldIds } from "../CustomFormField/CustomFormField";
import "../CustomTheme/customTheme.css";
import "./CustomRadioGroup.css";

const defaultGetOptionLabel = (item) => {
  if (item === null || item === undefined) return "";
  if (typeof item === "string" || typeof item === "number") return String(item);
  return item.name ?? item.label ?? item.title ?? String(item.value ?? item._id ?? item.id ?? "");
};

const defaultGetOptionValue = (item) => {
  if (item === null || item === undefined) return item;
  if (typeof item === "string" || typeof item === "number") return item;
  return item._id ?? item.id ?? item.value ?? item;
};

const RadioGroup = ({
  label,
  isRequired,
  colClass,
  className,
  style,
  options = [],
  value,
  onChange,
  direction = "row",
  disabled = false,
  name,
  getOptionLabel,
  getOptionValue,
  error,
  hint,
}) => {
  const autoId = useId();
  const groupName = name || `rdg-${autoId}`;
  const fieldIds = getFieldIds(groupName);

  const extractLabel = useCallback(
    (item) => (getOptionLabel ? getOptionLabel(item) : defaultGetOptionLabel(item)),
    [getOptionLabel]
  );
  const extractValue = useCallback(
    (item) => (getOptionValue ? getOptionValue(item) : defaultGetOptionValue(item)),
    [getOptionValue]
  );

  const hasValue = value !== undefined && value !== null && value !== "";
  const selectedValue = hasValue ? extractValue(value) : undefined;

  return (
    <CustomFormField
      label={label}
      isRequired={isRequired}
      htmlFor={groupName}
      hint={hint}
      error={error}
      colClass={colClass}
      className={`rdg-group${className ? ` ${className}` : ""}`}
      style={style}
    >
      <div
        className={`rdg-options rdg-${direction}${error ? " rdg-error" : ""}`}
        role="radiogroup"
        aria-label={label}
        aria-invalid={Boolean(error)}
        aria-describedby={fieldIds.describedBy(error, hint)}
      >
        {options.map((item, index) => {
          const optValue = extractValue(item);
          const checked = hasValue && optValue === selectedValue;
          const optionDisabled = disabled || item?.disabled;

          return (
            <label
              key={optValue ?? index}
              className={`rdg-option${checked ? " rdg-option-selected" : ""}${
                optionDisabled ? " rdg-option-disabled" : ""
              }`}
            >
              <input
                type="radio"
                id={index === 0 ? groupName : undefined} /* label click focuses the first option */
                className="rdg-input"
                name={groupName}
                checked={checked}
                disabled={optionDisabled}
                onChange={() => !optionDisabled && onChange?.(item)}
              />
              <span className="rdg-dot" />
              <span className="rdg-text">{extractLabel(item)}</span>
            </label>
          );
        })}
      </div>
    </CustomFormField>
  );
};

export default React.memo(RadioGroup);